import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { MapPin } from "lucide-react";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { apiFetch } from "@/service/api";

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const generatePastelColor = (id: number) => {
  const hue = (id * 137.5) % 360;
  return `hsl(${hue}, 60%, 40%)`;
};
const createColoredIcon = (color: string) => {
  const iconHtml = renderToStaticMarkup(
    <div className="relative flex items-center justify-center">
      <MapPin
        size={32}
        color={color}
        fill={color}
        fillOpacity={0.2}
        strokeWidth={2.5}
        className="drop-shadow-md"
      />
      <div
        className="absolute w-2 h-2 rounded-full bg-white border border-gray-200"
        style={{ top: "8px" }}
      />
    </div>,
  );
  return L.divIcon({
    className: "custom-leaflet-icon",
    html: iconHtml,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

export default function SurveyMap() {
  const { id } = useParams();
  const [coordinates, setCoordinates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [surveyColors, setSurveyColors] = useState<Record<number, string>>({});
  const [selectedDetail, setSelectedDetail] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  function formatearHora24a12(hora24: any) {
    if (!hora24) return "";
    const [h, m, s] = hora24.split(":");
    let hora = parseInt(h, 10);
    const ampm = hora >= 12 ? "PM" : "AM";
    hora = hora % 12;
    hora = hora ? hora : 12;
    return `${hora.toString().padStart(2, "0")}:${m}:${s} ${ampm}`;
  }

  const fetchDetail = async (coordId: number) => {
    setLoadingDetail(true);
    setSelectedDetail(null);
    try {
      const response = await apiFetch(
        `coordenadas-respuesta/detail/${coordId}`,
      );
      if (response.ok) {
        const data = await response.json();
        setSelectedDetail(data);
      }
    } catch (error) {
      console.error("Error fetching detail:", error);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    const fetchCoordinates = async () => {
      try {
        let url = "";
        if (id) url = `coordenadas-respuesta/find-by-survey`;
        else url = `coordenadas-respuesta/find-all`;
        const response = await apiFetch(url);
        if (response.ok) {
          const data = await response.json();
          setCoordinates(data);
          const uniqueSurveyIds = Array.from(
            new Set(
              data
                .map((c: any) => c.survey_response?.survey?.id)
                .filter(Boolean),
            ),
          );
          const colorMap: Record<number, string> = {};
          uniqueSurveyIds.forEach((sid: any) => {
            colorMap[sid] = generatePastelColor(Number(sid));
          });
          setSurveyColors(colorMap);
        }
      } catch (error) {
        console.error("Error fetching coordinates:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCoordinates();
  }, [id]);

  const center: [number, number] = [4.5709, -74.2973];
  const legendItems = Object.entries(surveyColors).map(([sid, color]) => {
    const survey = coordinates.find(
      (c) => c.survey_response?.survey?.id === Number(sid),
    )?.survey_response?.survey;
    return { id: sid, nombre: survey?.nombre || `Encuesta ${sid}`, color };
  });

  return (
    <div>
      <PageBreadcrumb pageTitle="Mapa de Respuestas" />
      <div className="min-h- rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <h3 className="font-semibold text-gray-800 text-center text-theme-xl dark:text-white/90 sm:text-2xl">
            Distribucion Geografica de Respuestas
          </h3>
        </div>
        {legendItems.length > 0 && (
          <div className="flex flex-wrap gap-3 justify-center bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
            {legendItems.map((item) => (
              <div key={item.id} className="flex items-center gap-2">
                <div
                  style={{ backgroundColor: item.color }}
                  className="w-3 h-3 rounded-full border border-white shadow-sm"
                ></div>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {item.nombre}
                </span>
              </div>
            ))}
          </div>
        )}
        <br />
        {loading ? (
          <div className="flex justify-center items-center h-">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
          </div>
        ) : coordinates.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            No hay coordenadas registradas para esta encuesta.
          </div>
        ) : (
          <div className="h- w-full rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-inner">
            <MapContainer
              center={center}
              zoom={5}
              style={{ height: "100%", width: "100%" }}
              attributionControl={false}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {coordinates.map((coord) => {
                const surveyId = coord.survey_response?.survey?.id;
                const color =
                  surveyColors[surveyId] || generatePastelColor(surveyId || 0);
                return (
                  <Marker
                    key={coord.id}
                    position={[Number(coord.latitud), Number(coord.longitud)]}
                    icon={createColoredIcon(color)}
                    eventHandlers={{ click: () => fetchDetail(coord.id) }}
                  >
                    <Popup>
                      <div className="p-2 min-w-">
                        {loadingDetail ? (
                          <div className="flex flex-col items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500 mb-2"></div>
                            <p className="text-xs text-gray-500">
                              Cargando detalles...
                            </p>
                          </div>
                        ) : selectedDetail ? (
                          <>
                            <div className="flex items-center gap-2 mb-2">
                              <div
                                style={{ backgroundColor: color }}
                                className="w-3 h-3 rounded-full border border-white shadow-sm"
                              ></div>
                              <h4 className="font-bold text-brand-600">
                                Encuesta:{" "}
                                {selectedDetail.survey_response?.survey.nombre}
                              </h4>
                            </div>
                            <p className="text-xs text-gray-600 mb-2 font-medium">
                              {selectedDetail.fecha} -{" "}
                              {formatearHora24a12(selectedDetail.hora)}
                            </p>
                            {selectedDetail.usuario && (
                              <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded mb-2 border border-gray-100 dark:border-gray-700">
                                <p className="text-xs text-gray-500 uppercase font-bold mb-1">
                                  Registrado por:
                                </p>
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                  {selectedDetail.usuario.nombres}{" "}
                                  {selectedDetail.usuario.apellidos}
                                </p>
                              </div>
                            )}
                            <div className="mt-2 border-t pt-2">
                              <p className="text-xs font-bold uppercase text-gray-400 mb-1">
                                Datos de la respuesta:
                              </p>
                              <div className="max-h-48 overflow-y-auto text-xs custom-scrollbar">
                                {selectedDetail.survey_response?.response &&
                                  Object.entries(
                                    selectedDetail.survey_response.response,
                                  ).map(([key, value]) => (
                                    <div
                                      key={key}
                                      className="mb-2 pb-1 border-b border-gray-50 last:border-0"
                                    >
                                      <div className="font-semibold text-gray-700 block mb-0.5">
                                        {key}:
                                      </div>
                                      <div className="text-gray-600 leading-relaxed">
                                        {typeof value === "object"
                                          ? JSON.stringify(value)
                                          : String(value)}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center gap-2 mb-2">
                            <div
                              style={{ backgroundColor: color }}
                              className="w-3 h-3 rounded-full border border-white shadow-sm"
                            ></div>
                            <h4 className="font-bold text-brand-600">
                              Encuesta: {coord.survey_response?.survey?.nombre}
                            </h4>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        )}
      </div>
    </div>
  );
}
